/**
 * ========== Player.tsx ==========
 * 播放器页面 - 相当于 C++ 中的"主控制器类"
 * 
 * 【类比 C++】
 * - 这个文件相当于一个"容器类"，组装多个子组件
 * - VideoPlayer 和 SubtitlePanel 就像 C++ 中的成员对象
 * - useState 相当于 private 成员变量
 * - 函数如 handleSeek 相当于成员函数
 */

// ==================== 导入区（相当于 C++ 的 #include） ====================
import { useState, useRef, useEffect, useMemo, useCallback } from "react";  // React 核心 Hooks
import { useLocation, useNavigate, useParams } from "react-router-dom";  // 路由相关
import { ArrowLeft, Heart, Loader2, Play, Pause, RotateCcw, Repeat1, Repeat, Bot, Mic, Monitor } from "lucide-react";  // 图标库
import { Button } from "@/components/ui/button";  // UI 组件
import { Badge } from "@/components/ui/badge";
import { VideoPlayer, VideoPlayerHandle } from "@/components/player/VideoPlayer";  // 视频播放器组件
import { SubtitlePanel } from "@/components/player/SubtitlePanel";  // 字幕面板组件
import { ShadowingPanel } from "@/components/player/ShadowingPanel"; // Shadowing Panel
import { ReadingSettings } from "@/components/player/ReadingSettings";
import { Sidebar } from "@/components/Sidebar";  // 侧边栏组件
import { useVideo } from "@/hooks/useVideos";  // 获取视频数据的 Hook
import { useSubtitles } from "@/hooks/useSubtitles";  // 获取字幕数据的 Hook
import { useLearningRecords } from "@/hooks/useLearningRecords";  // 学习记录 Hook
import { cn } from "@/lib/utils";  // 样式合并工具函数
import { useAuth } from "@/contexts/AuthContext";  // 用户认证上下文
import { useToast } from "@/hooks/use-toast";  // Toast 提示 Hook
import { useIsMobile } from "@/hooks/use-mobile";  // 检测是否移动端
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { AspectRatio } from "@radix-ui/react-aspect-ratio";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";

import { useVocabulary } from "@/hooks/useVocabulary";
import { useKuromoji } from "@/hooks/useKuromoji";
import { useMembershipStatus } from "@/hooks/useMembershipStatus";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { aiQuotaMonthKey } from "@/hooks/useAiQuota";
import { isLiveDemoBlockedVideo, readLiveDemoConfig } from "@/lib/live-demo-config";
import { readPlayerUiSettings, writePlayerUiSettings } from "@/lib/player-ui-settings";

type SentenceLoopTarget = {
  id: string;
  startTime: number;
  endTime: number;
};

// ==================== 主组件函数 ====================
/**
 * Player 组件 - 视频播放页面
 * 
 * 【React 函数组件 vs C++ 类】
 * C++:  class Player { private: int currentTime; public: void render(); }
 * React: function Player() { const [currentTime] = useState(); return JSX; }
 */
export default function Player() {
  // ========== 路由相关 ==========
  const navigate = useNavigate();  // 页面跳转函数，类似 C++ 中的页面切换
  const location = useLocation();
  const { videoId } = useParams<{ videoId: string }>();  // 从 URL 获取视频 ID，如 /player/abc123 中的 abc123
  
  // ========== useRef：引用，相当于 C++ 指针 ==========
  /**
   * playerRef 是对 VideoPlayer 组件的引用
   * 类比 C++: VideoPlayer* playerRef = nullptr;
   * 用途：让父组件能调用子组件的方法，如 playerRef.current->seekTo(10)
   */
  const playerRef = useRef<VideoPlayerHandle>(null);
  
  // ========== 用户认证 ==========
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { data: membership, isLoading: membershipLoading } = useMembershipStatus(user?.id);
  const liveQueryEnabled = useMemo(
    () => new URLSearchParams(location.search).get("live") === "1",
    [location.search],
  );
  const autoPlayFromQuery = useMemo(
    () => new URLSearchParams(location.search).get("autoplay") === "1",
    [location.search],
  );
  const isLiveDemoMode = useMemo(() => {
    if (!membership?.isAdmin) return false;
    return liveQueryEnabled;
  }, [liveQueryEnabled, membership?.isAdmin]);
  const initialUiSettings = useMemo(() => readPlayerUiSettings(), []);
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showFurigana, setShowFurigana] = useState(initialUiSettings.showFurigana ?? true);
  const [showRomaji, setShowRomaji] = useState(() => {
    if (typeof initialUiSettings.showRomaji === "boolean") {
      return initialUiSettings.showRomaji;
    }
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 430;
  });
  const [showChinese, setShowChinese] = useState(initialUiSettings.showChinese ?? true);
  const [isLiked, setIsLiked] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [sentenceLoop, setSentenceLoop] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(initialUiSettings.volume ?? 1);
  const [autoPlayNext, setAutoPlayNext] = useState(() => {
    if (typeof window === "undefined") return false;
    const raw = window.localStorage.getItem("player:auto-play-next");
    if (raw == null) return false;
    return raw === "1";
  });
  const [sentenceLoopTarget, setSentenceLoopTarget] = useState<SentenceLoopTarget | null>(null);
  const [focusSubtitles, setFocusSubtitles] = useState(false);
  
  // New States for Reading Settings
  const [fontSize, setFontSize] = useState(() => {
    if (typeof initialUiSettings.fontSize === "number") {
      return initialUiSettings.fontSize;
    }
    if (typeof window === "undefined") return 1.6;
    if (window.innerWidth < 390) return 0.9;
    return window.innerWidth < 768 ? 1.0 : 1.6;
  });
  const [blurStrength, setBlurStrength] = useState(initialUiSettings.blurStrength ?? 0);

  // ========== 数据获取 Hook ==========
  /**
   * 从数据库获取视频和字幕数据
   * 
   * 类比 C++ 的异步请求:
   *   auto video = await fetchVideo(videoId);
   *   bool isLoading = !video.ready();
   */
  const { data: video, isLoading: videoLoading } = useVideo(videoId || "");
  const [playUrl, setPlayUrl] = useState<string | null>(null);
  const [isUrlLoading, setIsUrlLoading] = useState(false);

  // Fetch secure play URL
  useEffect(() => {
    if (!videoId || !user || !video) return;
    
    // Define the recursive fetch function
    const fetchPlayUrl = async (params: any = {}) => {
      setIsUrlLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-video-play-url', {
          body: { videoId, ...params }
        });

        if (error || data?.error) {
           const errMsg = error?.message || data?.error;
           if (errMsg === "CAPTCHA_REQUIRED") {
               toast({
                   title: "访问频繁",
                   description: "请完成验证后继续观看",
               });
               try {
                   // Dynamic import to avoid SSR issues if any
                   const { runAlicomCaptcha } = await import("@/lib/aliyun-captcha");
                   const captchaRes = await runAlicomCaptcha();
                   // Recursive retry with captcha params
                   return fetchPlayUrl(captchaRes);
               } catch (e) {
                   console.error("Captcha failed or cancelled", e);
                   setIsUrlLoading(false);
                   return;
               }
           }
           throw error || new Error(data?.error);
        }

        if (data?.playUrl) {
          setPlayUrl(data.playUrl);
        }
      } catch (err) {
        console.error("Failed to fetch play URL:", err);
        const msg =
          (err as any)?.context?.body?.error ||
          (err as any)?.message ||
          "请刷新重试";
        toast({ 
          title: "获取播放地址失败", 
          description: msg, 
          variant: "destructive" 
        });
      } finally {
        setIsUrlLoading(false);
      }
    };

    fetchPlayUrl();
  }, [videoId, user, video]);

  // Handle play URL from video object if fetch fails or not needed
  useEffect(() => {
    if (video?.videoUrl && !playUrl) {
        setPlayUrl(video.videoUrl);
    }
  }, [video, playUrl]);

  const subtitleSource =
    video?.contentType === "audio" ? "audio" : "auto";
  const { data: subtitles = [], isLoading: subtitlesLoading } = useSubtitles(videoId || "", subtitleSource);
  const { vocabulary } = useVocabulary();
  const { tokenizer, error: tokenizerError } = useKuromoji(); // Load tokenizer at parent level

  // 学习记录 Hook - 自动保存进度
  const { loadLastPosition } = useLearningRecords({
    videoId: videoId || "",
    duration,
    currentTime,
  });

  // 加载上次播放位置
  useEffect(() => {
    if (!videoId || videoLoading || isUrlLoading) return;
    if (liveQueryEnabled && membershipLoading) return;
    if (!isLiveDemoMode && !user) return;

    const restoreKey = `${isLiveDemoMode ? "live" : "normal"}:${videoId}:${user?.id ?? "guest"}`;
    if (restoredPositionForVideoRef.current === restoreKey) return;
    restoredPositionForVideoRef.current = restoreKey;

    // Live demo must always start from 0 to look natural and avoid inheriting history position.
    if (isLiveDemoMode) {
      setCurrentTime(0);
      const t = window.setTimeout(() => {
        playerRef.current?.seekTo(0);
      }, 80);
      return () => window.clearTimeout(t);
    }

    let cancelled = false;
    let retrySeekTimer: number | null = null;
    const loadPosition = async () => {
      const lastPosition = await loadLastPosition();
      if (cancelled) return;
      if (lastPosition && lastPosition > 5) {
        setCurrentTime(lastPosition);
        playerRef.current?.seekTo(lastPosition);
        // Signed/play URLs may finalize slightly later; re-seek once to avoid falling back to 0.
        retrySeekTimer = window.setTimeout(() => {
          if (cancelled) return;
          playerRef.current?.seekTo(lastPosition);
        }, 280);
        toast({
          title: "继续上次播放",
          description: `已跳转到上次观看位置 ${Math.floor(lastPosition / 60)}:${Math.floor(lastPosition % 60).toString().padStart(2, "0")}`,
        });
      }
    };
    void loadPosition();
    return () => {
      cancelled = true;
      if (retrySeekTimer != null) {
        window.clearTimeout(retrySeekTimer);
      }
    };
  }, [isLiveDemoMode, isUrlLoading, liveQueryEnabled, loadLastPosition, membershipLoading, user, videoId, videoLoading]);

  // Check initial content type
  useEffect(() => {
    if (!video) return;

    let isMounted = true;

    const performCheck = async () => {
        // Default based on content type
        let shouldFocusSubtitles = video.contentType === 'audio';

        // Check series override
        if (video.series) {
            try {
                const { data } = await supabase
                    .from('series')
                    .select('content_type')
                    .eq('id', video.series)
                    .maybeSingle();
                
                if (data?.content_type === 'audio') {
                    shouldFocusSubtitles = true;
                }
            } catch (e) {
                console.error("Error checking series type", e);
            }
        }

        if (isMounted) {
            setFocusSubtitles(shouldFocusSubtitles);
        }
    };

    performCheck();

    return () => {
        isMounted = false;
    };
  }, [video]); // 仅当 video 对象变化时执行

  // Check initial like status
  useEffect(() => {
    if (!user || !videoId) return;
    const checkLikeStatus = async () => {
      try {
        const { count, error } = await supabase
          .from('user_favorites')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('video_id', videoId);
        
        if (error) {
          return;
        }
        setIsLiked(!!count);
      } catch (e) {
        console.error("Check like status exception:", e);
      }
    };
    checkLikeStatus();
  }, [user, videoId]);

  useEffect(() => {
    autoPlayLockRef.current = false;
    endedAutoAdvanceRef.current = false;
    liveBlockedSkipRef.current = false;
    restoredPositionForVideoRef.current = null;
    if (liveAdvanceWatchdogRef.current != null) {
      window.clearTimeout(liveAdvanceWatchdogRef.current);
      liveAdvanceWatchdogRef.current = null;
    }
  }, [videoId]);

  const toggleLike = async () => {
    if (!user) {
      toast({ title: "请先登录", description: "登录后即可收藏视频" });
      return;
    }
    
    const newStatus = !isLiked;
    setIsLiked(newStatus); // Optimistic update

    try {
      if (newStatus) {
        const { error } = await supabase
          .from('user_favorites')
          .insert({ user_id: user.id, video_id: videoId });
        if (error) throw error;
        toast({ title: "已收藏" });
      } else {
        const { error } = await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('video_id', videoId);
        if (error) throw error;
        toast({ title: "已取消收藏" });
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      setIsLiked(!newStatus); // Rollback
      toast({ 
        title: "操作失败", 
        description: "请稍后重试", 
        variant: "destructive" 
      });
    }
  };
  const [fullLoop, setFullLoop] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any | null>(null);
  const analyzeInFlightRef = useRef<Promise<void> | null>(null);
  const autoPlayLockRef = useRef(false);
  const endedAutoAdvanceRef = useRef(false);
  const liveBlockedSkipRef = useRef(false);
  const liveAdvanceWatchdogRef = useRef<number | null>(null);
  const autoStartPendingRef = useRef(false);
  const liveAnalyzeRef = useRef<() => Promise<void> | void>(() => undefined);
  const liveSaveSentenceRef = useRef<() => Promise<void> | void>(() => undefined);
  const restoredPositionForVideoRef = useRef<string | null>(null);
  const resumeAfterAnalysisRef = useRef(false);
  const [isPlayingUI, setIsPlayingUI] = useState(false);

  // New Reading Settings States
  const [hideUnderline, setHideUnderline] = useState(initialUiSettings.hideUnderline ?? false);
  const [hideGrammarBackground, setHideGrammarBackground] = useState(initialUiSettings.hideGrammarBackground ?? false);
  const [hideShadowingSpotlight, setHideShadowingSpotlight] = useState(initialUiSettings.hideShadowingSpotlight ?? false);
  const [shadowingMode, setShadowingMode] = useState(false);
  const [showOnlyChinese, setShowOnlyChinese] = useState(initialUiSettings.showOnlyChinese ?? false);
  const [highlightNewWords, setHighlightNewWords] = useState(initialUiSettings.highlightNewWords ?? true);
  const [theme, setTheme] = useState(initialUiSettings.theme ?? "system");
  const shouldSuspendVideo = analysisOpen || isSettingsOpen || shadowingMode;

  useEffect(() => {
    return () => {
      if (liveAdvanceWatchdogRef.current != null) {
        window.clearTimeout(liveAdvanceWatchdogRef.current);
        liveAdvanceWatchdogRef.current = null;
      }
    };
  }, []);

  // Apply theme
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("player:auto-play-next", autoPlayNext ? "1" : "0");
  }, [autoPlayNext]);

  useEffect(() => {
    const normalizedTheme = theme === "light" || theme === "dark" ? theme : "system";
    writePlayerUiSettings({
      showFurigana,
      showRomaji,
      showChinese,
      hideUnderline,
      hideGrammarBackground,
      hideShadowingSpotlight,
      showOnlyChinese,
      highlightNewWords,
      fontSize,
      blurStrength,
      volume,
      theme: normalizedTheme,
    });
  }, [
    blurStrength,
    fontSize,
    hideGrammarBackground,
    hideShadowingSpotlight,
    hideUnderline,
    highlightNewWords,
    showChinese,
    showFurigana,
    showOnlyChinese,
    showRomaji,
    theme,
    volume,
  ]);

  useEffect(() => {
    if (!isMobile || !isSettingsOpen) return;
    playerRef.current?.pause();
  }, [isMobile, isSettingsOpen]);


  useEffect(() => {
    const routeState = location.state as { autoPlay?: boolean } | null;
    if (!routeState?.autoPlay) return;
    autoStartPendingRef.current = true;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!autoPlayFromQuery) return;
    autoStartPendingRef.current = true;
  }, [autoPlayFromQuery, videoId]);

  useEffect(() => {
    if (!autoStartPendingRef.current) return;
    if (videoLoading || isUrlLoading || !playUrl) return;

    const timer = window.setTimeout(() => {
      playerRef.current?.play();
      autoStartPendingRef.current = false;
    }, 0);

    return () => window.clearTimeout(timer);
  }, [videoLoading, isUrlLoading, playUrl, videoId]);



  // 任一数据在加载中都显示加载状态 (Subtitles loading should not block player)
  const isLoading = videoLoading || isUrlLoading;

  const findSubtitleAtTime = useCallback(
    (time: number) => {
      let left = 0;
      let right = subtitles.length - 1;

      while (left <= right) {
        const mid = (left + right) >> 1;
        const sub = subtitles[mid];

        if (time < sub.startTime) {
          right = mid - 1;
        } else if (time >= sub.endTime) {
          left = mid + 1;
        } else {
          return sub;
        }
      }

      return undefined;
    },
    [subtitles],
  );

  const toSentenceLoopTarget = useCallback(
    (sub: { id: string; startTime: number; endTime: number } | undefined) => {
      if (!sub) return null;
      return {
        id: sub.id,
        startTime: sub.startTime,
        endTime: sub.endTime,
      } as SentenceLoopTarget;
    },
    [],
  );

  const currentSubtitle = useMemo(() => findSubtitleAtTime(currentTime), [findSubtitleAtTime, currentTime]);

  useEffect(() => {
    if (!sentenceLoop) {
      if (sentenceLoopTarget) setSentenceLoopTarget(null);
      return;
    }

    if (!sentenceLoopTarget && currentSubtitle) {
      setSentenceLoopTarget(toSentenceLoopTarget(currentSubtitle));
    }
  }, [sentenceLoop, sentenceLoopTarget, currentSubtitle, toSentenceLoopTarget]);

  useEffect(() => {
    const activeLoopTarget =
      sentenceLoopTarget || (sentenceLoop ? toSentenceLoopTarget(currentSubtitle) : null);

    if (sentenceLoop && activeLoopTarget) {
      // Keep a safe threshold so low-frequency timeupdate events won't skip loop rewind.
      const loopThreshold = Math.max(activeLoopTarget.endTime - 0.12, activeLoopTarget.startTime + 0.03);

      if (currentTime >= loopThreshold && currentTime <= activeLoopTarget.endTime + 0.6) {
        playerRef.current?.seekTo(activeLoopTarget.startTime);
        setCurrentTime(activeLoopTarget.startTime);
        return;
      }

      // If user seeks far away while loop is still on, lock to the new sentence.
      if (currentTime < activeLoopTarget.startTime - 0.8 || currentTime > activeLoopTarget.endTime + 0.8) {
        const nextTarget = toSentenceLoopTarget(findSubtitleAtTime(currentTime));
        if (nextTarget && nextTarget.id !== activeLoopTarget.id) {
          setSentenceLoopTarget(nextTarget);
        }
      }
    }

    if (fullLoop && duration > 0 && currentTime >= duration - 0.05) {
      playerRef.current?.seekTo(0);
      setCurrentTime(0);
    }
  }, [
    currentTime,
    sentenceLoop,
    sentenceLoopTarget,
    currentSubtitle,
    toSentenceLoopTarget,
    findSubtitleAtTime,
    fullLoop,
    duration,
  ]);

  const handleAnalyzeSentence = async () => {
    if (!user) {
      toast({ title: "请先登录", description: "登录后即可使用 AI 整句解析" });
      return;
    }
    if (!currentSubtitle) {
      toast({ title: "暂无可解析字幕", description: "请播放到有字幕的时间点再试" });
      return;
    }

    // Keep analysis interaction predictable on mobile/desktop:
    // pause playback first, then open the dialog.
    resumeAfterAnalysisRef.current = isPlayingUI;
    playerRef.current?.pause();
    setIsPlayingUI(false);
    setAnalysisOpen(true);
    if (analyzeInFlightRef.current) return;

    setIsAnalyzing(true);
    setAnalysisResult(null);

    const p = (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("analyze-sentence", {
          body: { text: currentSubtitle.japanese },
        });
        if (error) throw error;
        if ((data as any)?.error) throw new Error((data as any).error);
        const quota = (data as any)?._quota;
        if (user && quota) {
          const month = aiQuotaMonthKey();
          const key = ["ai-quota", user.id, month];
          const prev = queryClient.getQueryData(key) as any;
          if (prev) {
            queryClient.setQueryData(key, {
              ...prev,
              lookupRemaining: Number(quota.lookupRemaining ?? prev.lookupRemaining),
              analyzeRemaining: Number(quota.analyzeRemaining ?? prev.analyzeRemaining),
            });
          } else {
            queryClient.invalidateQueries({ queryKey: key });
          }
        }
        setAnalysisResult(data);
      } catch (err) {
        console.error("Analyze sentence failed:", err);
      const status = (err as any)?.context?.status as number | undefined;
      const rawMsg =
        (err as any)?.context?.body?.error ||
        (err as any)?.context?.body?.message ||
        (err as any)?.message ||
        "请稍后重试";
      const msg =
        typeof rawMsg === "string" &&
        rawMsg.includes("Edge Function returned a non-2xx status code")
          ? status === 429
            ? "请求过于频繁，请稍后再试"
            : status === 503
              ? "AI 服务繁忙，请稍后再试"
              : "服务暂不可用，请稍后再试"
          : rawMsg;
        toast({ title: "解析失败", description: msg, variant: "destructive" });
      } finally {
        setIsAnalyzing(false);
        analyzeInFlightRef.current = null;
      }
    })();

  analyzeInFlightRef.current = p;
  await p;
  };

  const handleAnalysisOpenChange = useCallback(
    (open: boolean) => {
      setAnalysisOpen(open);
      if (open) return;

      const shouldResume = resumeAfterAnalysisRef.current;
      resumeAfterAnalysisRef.current = false;
      if (!shouldResume) return;
      if (isSettingsOpen || shadowingMode) return;

      window.setTimeout(() => {
        playerRef.current?.play();
      }, 0);
    },
    [isSettingsOpen, shadowingMode],
  );

  useEffect(() => {
    liveAnalyzeRef.current = handleAnalyzeSentence;
  }, [handleAnalyzeSentence]);

  useEffect(() => {
    liveSaveSentenceRef.current = async () => {
      if (!user || !currentSubtitle) return;
      try {
        const { error } = await supabase.from("vocabulary").insert({
          user_id: user.id,
          word: currentSubtitle.japanese,
          meaning: currentSubtitle.chinese,
          romaji: currentSubtitle.romaji || null,
          source_video_id: videoId || null,
        });
        if (error) {
          // Ignore duplicate or intermittent write failures in live demo mode.
          return;
        }
      } catch {
        // ignore in demo runner
      }
    };
  }, [currentSubtitle, user, videoId]);

  // ========== 事件处理函数（相当于 C++ 成员函数） ==========
  
  /**
   * 跳转到指定时间
   * @param time - 目标时间（秒）
   * 
   * 调用子组件 VideoPlayer 的 seekTo 方法
   * 类比 C++: playerRef->seekTo(time);
   */
  const handleSeek = (time: number) => {
    playerRef.current?.seekTo(time);  // ?. 是可选链，防止 null 报错
    setCurrentTime(time);
    if (sentenceLoop) {
      const nextTarget = toSentenceLoopTarget(findSubtitleAtTime(time));
      if (nextTarget) {
        setSentenceLoopTarget(nextTarget);
      }
    }
  };

  const toggleSentenceLoop = () => {
    setSentenceLoop((prev) => {
      const next = !prev;
      if (!next) {
        setSentenceLoopTarget(null);
        return next;
      }
      const target = toSentenceLoopTarget(currentSubtitle || findSubtitleAtTime(currentTime));
      if (target) {
        setSentenceLoopTarget(target);
      }
      return next;
    });
  };

  const handleBackToSeries = () => {
    if (video?.series) {
      navigate(isLiveDemoMode ? `/series/${video.series}?live=1` : `/series/${video.series}`);
      return;
    }
    navigate(isLiveDemoMode ? "/?live=1" : -1);
  };

  const togglePlayUI = () => {
    if (!playerRef.current) return;
    if (isPlayingUI) {
      playerRef.current.pause();
      // setIsPlayingUI(false); // Remove this line, rely on onPlay/onPause callback
    } else {
      playerRef.current.play();
      // setIsPlayingUI(true); // Remove this line, rely on onPlay/onPause callback
    }
  };
  const rewind5UI = () => {
    const t = Math.max(0, currentTime - 5);
    handleSeek(t);
  };

  /**
   * 登出处理
   */
  const handleLogout = async () => {
    await signOut();
    toast({ title: "已退出登录" });
    navigate("/");  // 跳转到首页
  };

  /**
   * 侧边栏导航处理
   * @param page - 目标页面标识
   */
  const handleNavigate = (page: string) => {
    if (page === "home") navigate("/");
    else if (page === "vocabulary") navigate("/vocabulary");
    else if (page === "favorites") navigate("/favorites");
    else if (page === "history") navigate("/history");
    else if (page === "feedback") navigate("/feedback");
    else if (page === "profile") navigate("/profile");
  };

  // Shadowing Mode Handlers
  const handlePrevSentence = () => {
    if (!subtitles.length) return;
    const currentIndex = subtitles.findIndex(s => currentTime >= s.startTime && currentTime < s.endTime);
    if (currentIndex > 0) {
      handleSeek(subtitles[currentIndex - 1].startTime);
    } else {
        const prev = [...subtitles].reverse().find(s => s.endTime < currentTime);
        if (prev) handleSeek(prev.startTime);
        else handleSeek(0);
    }
  };

  const handleNextSentence = () => {
     if (!subtitles.length) return;
     const currentIndex = subtitles.findIndex(s => currentTime >= s.startTime && currentTime < s.endTime);
     if (currentIndex !== -1 && currentIndex < subtitles.length - 1) {
       handleSeek(subtitles[currentIndex + 1].startTime);
     } else {
        const next = subtitles.find(s => s.startTime > currentTime);
        if (next) handleSeek(next.startTime);
     }
  };

  const handleAutoPlayNext = useCallback(async () => {
    if (!autoPlayNext && !isLiveDemoMode) return;
    if (!videoId || !video) return;
    if (autoPlayLockRef.current) return;

    autoPlayLockRef.current = true;
    let didNavigate = false;

    try {
      const currentVideoId = String(videoId);
      const liveConfig = isLiveDemoMode ? readLiveDemoConfig() : null;
      const isBlockedForLive = (item: { id: string; title?: string | null }) =>
        !!liveConfig && isLiveDemoBlockedVideo(liveConfig, item.id, item.title);
      const currentPath = `${location.pathname}${location.search}`;
      const navigateTo = (
        path: string,
        options?: Parameters<typeof navigate>[1],
      ) => {
        if (path === currentPath) return false;
        didNavigate = true;
        navigate(path, options);
        return true;
      };

      let data: Array<{ id: string | number; title?: string | null }> | null = null;
      let error: any = null;

      if (video.contentType === "audio") {
        let query = supabase
          .from("audios")
          .select("id, title, series_id, created_at");

        if (video.series) {
          query = query.eq("series_id", video.series);
        }

        const result = await query
          .order("created_at", { ascending: true })
          .order("id", { ascending: true });
        data = result.data as Array<{ id: string | number; title?: string | null }> | null;
        error = result.error;
      } else {
        let query = supabase
          .from("videos")
          .select("id, title, series_id, created_at")
          .eq("is_published", true);

        if (video.series) {
          query = query.eq("series_id", video.series);
        }

        const result = await query
          .order("created_at", { ascending: true })
          .order("id", { ascending: true });
        data = result.data as Array<{ id: string | number; title?: string | null }> | null;
        error = result.error;
      }

      if (error) throw error;

      const ordered = ((data || []) as Array<{ id: string | number; title?: string | null }>).map((item) => ({
        id: String(item.id),
        title: item.title ?? null,
      }));
      const currentIndex = ordered.findIndex((item) => item.id === currentVideoId);
      const nextCandidate =
        currentIndex >= 0
          ? ordered.slice(currentIndex + 1).find((item) => !isBlockedForLive(item))
          : undefined;
      const nextId = nextCandidate?.id;
      const recoveryCandidate = ordered.find((item) => !isBlockedForLive(item));
      const recoveryId = currentIndex < 0 ? recoveryCandidate?.id : undefined;
      const liveHomePath =
        video.series && isLiveDemoMode
          ? `/?live=1&excludeSeries=${encodeURIComponent(video.series)}`
          : "/?live=1";

      if (nextId) {
        if (isLiveDemoMode) {
          const liveConfig = readLiveDemoConfig();
          // In live demo mode, sometimes go back to home to mimic browsing behavior.
          const shouldBrowseHome = Math.random() < liveConfig.playerGoHomeChance;
          if (shouldBrowseHome) {
            if (navigateTo(liveHomePath, { replace: true })) return;
          }
        }

        const nextPath = isLiveDemoMode
          ? `/player/${nextId}?live=1&autoplay=1`
          : `/player/${nextId}`;
        if (navigateTo(nextPath, { state: { autoPlay: true } })) return;
      }

      if (recoveryId && recoveryId !== currentVideoId) {
        const recoverPath = isLiveDemoMode
          ? `/player/${recoveryId}?live=1&autoplay=1`
          : `/player/${recoveryId}`;
        if (navigateTo(recoverPath, { state: { autoPlay: true } })) return;
      }

      if (isLiveDemoMode) {
        if (navigateTo(liveHomePath, { replace: true })) return;

        // Router no-op fallback: force jump out of current player route.
        const fallbackPath = video.series
          ? `/series/${video.series}?live=1`
          : "/?live=1";
        if (navigateTo(fallbackPath, { replace: true })) return;
      }

      autoPlayLockRef.current = false;
    } catch (error) {
      console.error("Auto play next failed:", error);
      if (isLiveDemoMode) {
        const liveHomePath =
          video?.series ? `/?live=1&excludeSeries=${encodeURIComponent(video.series)}` : "/?live=1";
        didNavigate = true;
        navigate(liveHomePath, { replace: true });
        return;
      }
      autoPlayLockRef.current = false;
    } finally {
      if (!didNavigate) {
        autoPlayLockRef.current = false;
      }
    }
  }, [autoPlayNext, isLiveDemoMode, location.pathname, location.search, navigate, video, videoId]);

  useEffect(() => {
    if (!isLiveDemoMode || !video) return;
    if (liveBlockedSkipRef.current) return;

    const liveConfig = readLiveDemoConfig();
    if (!isLiveDemoBlockedVideo(liveConfig, videoId, (video as any)?.title)) return;

    liveBlockedSkipRef.current = true;
    const timer = window.setTimeout(() => {
      void handleAutoPlayNext();
    }, 400);

    return () => window.clearTimeout(timer);
  }, [handleAutoPlayNext, isLiveDemoMode, video, videoId]);

  useEffect(() => {
    if (!(autoPlayNext || isLiveDemoMode)) return;
    if (endedAutoAdvanceRef.current) return;
    if (!isLiveDemoMode && !isPlayingUI) return;
    if (fullLoop || sentenceLoop) return;
    if (!duration || duration <= 0) return;

    const nearEndThreshold = isLiveDemoMode
      ? Math.max(2, Math.min(8, duration * 0.04))
      : Math.max(0.25, Math.min(1, duration * 0.01));
    if (currentTime >= duration - nearEndThreshold) {
      endedAutoAdvanceRef.current = true;
      void handleAutoPlayNext();
      if (isLiveDemoMode) {
        if (liveAdvanceWatchdogRef.current != null) {
          window.clearTimeout(liveAdvanceWatchdogRef.current);
          liveAdvanceWatchdogRef.current = null;
        }
        const sourceVideoId = String(videoId || "");
        const sourceSeries = video?.series || "";
        liveAdvanceWatchdogRef.current = window.setTimeout(() => {
          // If still on the same player route, force leave this page to avoid "stuck at ended".
          const stillOnSameVideo = window.location.pathname === `/player/${sourceVideoId}`;
          if (!stillOnSameVideo) return;
          autoPlayLockRef.current = false;
          endedAutoAdvanceRef.current = false;
          const fallbackPath = sourceSeries
            ? `/?live=1&excludeSeries=${encodeURIComponent(sourceSeries)}`
            : "/?live=1";
          navigate(fallbackPath, { replace: true });
        }, 5200);
      }
    }
  }, [
    autoPlayNext,
    currentTime,
    duration,
    fullLoop,
    handleAutoPlayNext,
    isLiveDemoMode,
    isPlayingUI,
    navigate,
    sentenceLoop,
    video?.series,
    videoId,
  ]);

  useEffect(() => {
    if (!isLiveDemoMode) return;
    if (!videoId || !video) return;
    if (videoLoading || isUrlLoading || !playUrl) return;

    let stopped = false;
    const timers: number[] = [];

    const schedule = (fn: () => void | Promise<void>, delayMs: number) => {
      const t = window.setTimeout(() => {
        if (stopped) return;
        void fn();
      }, delayMs);
      timers.push(t);
    };

    const rawDurationSeconds = Math.max(duration, Number((video as any).durationSeconds || 0));
    const maxSeconds = Number.isFinite(rawDurationSeconds) && rawDurationSeconds > 0 ? rawDurationSeconds : 90;
    // Live demo default watch window: 70% ~ 100% of current video duration.
    const watchRatio = 0.7 + Math.random() * 0.3;
    const watchWindowMs = Math.max(30000, Math.round(maxSeconds * watchRatio * 1000));
    const liveConfig = readLiveDemoConfig();

    // Start playback immediately after entering player.
    playerRef.current?.play();

    // AI sentence analysis: default once per video, configurable via live settings.
    const aiCount = Math.max(0, Math.floor(liveConfig.aiPerVideoCount));
    if (aiCount > 0) {
      const safeSlots = Math.max(1, aiCount);
      for (let i = 0; i < safeSlots; i += 1) {
        const slotRatio = (i + 1) / (safeSlots + 1);
        const jitter = (Math.random() - 0.5) * 0.16;
        const ratio = Math.min(0.92, Math.max(0.24, slotRatio + jitter));
        const analyzeAtMs = Math.min(watchWindowMs - 8000, Math.max(9000, Math.round(watchWindowMs * ratio)));
        schedule(async () => {
          await liveAnalyzeRef.current?.();
          const closeDelay = 2600 + Math.floor(Math.random() * 1800);
          const t = window.setTimeout(() => {
            if (stopped) return;
            setAnalysisOpen(false);
            playerRef.current?.play();
          }, closeDelay);
          timers.push(t);
        }, analyzeAtMs);
      }
    }

    // Subtitle list occasional scroll actions to mimic natural interaction.
    if (Math.random() < liveConfig.subtitleScrollChance) {
      const actionMin = Math.max(0, Math.min(liveConfig.subtitleScrollActionsMin, liveConfig.subtitleScrollActionsMax));
      const actionMax = Math.max(liveConfig.subtitleScrollActionsMin, liveConfig.subtitleScrollActionsMax);
      const actionCount = actionMin + Math.floor(Math.random() * (actionMax - actionMin + 1));
      for (let i = 0; i < actionCount; i += 1) {
        const atMs = Math.max(
          3500,
          Math.floor((watchWindowMs * (i + 1)) / (actionCount + 1) + (Math.random() - 0.5) * 6000),
        );
        schedule(() => {
          const container = document.querySelector<HTMLElement>('[data-live-scroll="subtitle-list"]');
          if (!container) return;
          const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
          if (maxScrollTop <= 0) return;

          const deltaBase = Math.max(50, Math.floor(container.clientHeight * (0.18 + Math.random() * 0.28)));
          const direction = Math.random() < 0.35 ? -1 : 1;
          const delta = direction * deltaBase;
          const targetTop = Math.max(0, Math.min(maxScrollTop, container.scrollTop + delta));
          container.scrollTo({ top: targetTop, behavior: "smooth" });
        }, atMs);
      }
    }

    // Optional sentence save action.
    if (Math.random() < 0.4) {
      schedule(async () => {
        await liveSaveSentenceRef.current?.();
      }, 18000 + Math.floor(Math.random() * 22000));
    }

    // Optional seek once to mimic manual browsing behavior.
    if (Math.random() < 0.55) {
      schedule(() => {
        const target = Math.max(0, Math.min(maxSeconds - 0.25, (0.2 + Math.random() * 0.6) * maxSeconds));
        playerRef.current?.seekTo(target);
        setCurrentTime(target);
        playerRef.current?.play();
      }, 24000 + Math.floor(Math.random() * 18000));
    }

    // Switch content after a normal watch window, not every few seconds.
    schedule(() => {
      const roll = Math.random();
      if (roll < 0.12) {
        navigate("/favorites?live=1");
        return;
      }
      if (roll < 0.2) {
        navigate("/profile?live=1");
        return;
      }
      if (video.series) {
        navigate(`/series/${video.series}?live=1`);
        return;
      }
      navigate("/?live=1");
    }, watchWindowMs);

    return () => {
      stopped = true;
      for (const t of timers) {
        window.clearTimeout(t);
      }
    };
  }, [duration, isLiveDemoMode, isUrlLoading, navigate, playUrl, video, videoId, videoLoading]);


  // ========== 常量配置 ==========
  /**
   * JLPT 等级对应的颜色配置
   * 类比 C++: std::map<string, ColorConfig> levelConfig;
   */
  const levelConfig: Record<string, { bg: string; text: string }> = {
    N5: { bg: "bg-emerald-500/15", text: "text-emerald-600" },
    N4: { bg: "bg-sky-500/15", text: "text-sky-600" },
    N3: { bg: "bg-amber-500/15", text: "text-amber-600" },
    N2: { bg: "bg-orange-500/15", text: "text-orange-600" },
    N1: { bg: "bg-rose-500/15", text: "text-rose-600" },
  };
  const posColorMap: Record<string, string> = {
    名詞: "bg-emerald-100 text-emerald-700 border-emerald-200",
    動詞: "bg-sky-100 text-sky-700 border-sky-200",
    形容詞: "bg-amber-100 text-amber-700 border-amber-200",
    副詞: "bg-purple-100 text-purple-700 border-purple-200",
    助詞: "bg-pink-100 text-pink-700 border-pink-200",
    助動詞: "bg-rose-100 text-rose-700 border-rose-200",
    連体詞: "bg-teal-100 text-teal-700 border-teal-200",
    接続詞: "bg-indigo-100 text-indigo-700 border-indigo-200",
    感動詞: "bg-orange-100 text-orange-700 border-orange-200",
    記号: "bg-slate-100 text-slate-700 border-slate-200",
  };
  const posLegend = [
    { label: "名詞", className: posColorMap["名詞"] },
    { label: "動詞", className: posColorMap["動詞"] },
    { label: "形容詞", className: posColorMap["形容詞"] },
    { label: "副詞", className: posColorMap["副詞"] },
    { label: "助詞", className: posColorMap["助詞"] },
    { label: "助動詞", className: posColorMap["助動詞"] },
    { label: "連体詞", className: posColorMap["連体詞"] },
    { label: "接続詞", className: posColorMap["接続詞"] },
    { label: "感動詞", className: posColorMap["感動詞"] },
    { label: "記号", className: posColorMap["記号"] },
  ];

  // ========== 条件渲染：加载状态 ==========
  /**
   * 类比 C++:
   *   if (isLoading) { renderLoadingScreen(); return; }
   */
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">加载中...</span>
      </div>
    );
  }

  // ========== 条件渲染：视频不存在 ==========
  if (!video) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-6 bg-card rounded-lg shadow-sm border">
          <p className="text-muted-foreground mb-4 text-lg">视频不存在或已被删除</p>
          <p className="text-xs text-muted-foreground mb-6 font-mono bg-muted p-2 rounded">
            Video ID: {videoId || "undefined"}
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => navigate("/")}>返回首页</Button>
            <Button variant="outline" onClick={() => window.location.reload()}>刷新页面</Button>
          </div>
        </div>
      </div>
    );
  }
  // 获取当前视频的等级颜色配置
  const level = levelConfig[video.jlptLevel] ?? { bg: "bg-muted", text: "text-muted-foreground" };

  // ========== JSX 返回：组件渲染 ==========
  /**
   * JSX 相当于 C++ 中的 render() 函数返回的 UI 描述
   * 
   * JSX 语法：
   * - 看起来像 HTML，但可以嵌入 JavaScript 表达式 {expression}
   * - className 替代 class（因为 class 是 JS 保留字）
   * - 事件用 onClick, onChange 等（驼峰命名）
   */
  return (
    <div className={cn("min-h-screen bg-background flex flex-col", isMobile && "h-[var(--app-dvh)] overflow-hidden")}>
      {/* ===== 侧边栏组件 ===== */}
      {/* 
        组件调用相当于 C++ 的对象实例化 + 方法调用
        <Sidebar prop1={value1} /> 类似 C++ 的 Sidebar sidebar(value1);
      */}
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentPage=""
        onNavigate={handleNavigate}
        isLoggedIn={!!user}  // !! 转换为布尔值
        onLogout={handleLogout}
      />

      {/* ===== 顶部导航栏 ===== */}
      <header className={cn(
        "sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border transition-all duration-300",
        "lg:ml-16",
        !sidebarCollapsed && "lg:ml-60"
      )}>
        <div className="px-2.5 sm:px-3 lg:px-4 h-10 sm:h-11 lg:h-14 flex items-center justify-between max-w-[1920px] mx-auto">
          {/* 左侧：返回按钮 + 视频标题 */}
          <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-4 min-w-0 flex-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToSeries}
              className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 shrink-0"
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {/* 视频标题 */}
              <h1 className="font-bold text-xs sm:text-sm lg:text-base text-foreground line-clamp-1">
                {video.title}
              </h1>
              {isLiveDemoMode ? (
                <span className="hidden sm:inline-flex rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                  直播演示
                </span>
              ) : null}
            </div>
          </div>

          {/* 右侧：设置 */}
          <div className="flex items-center gap-1 shrink-0">
          </div>
        </div>
      </header>

      {/* ===== 主内容区 ===== */}
      <main className={cn(
        "flex-1 min-h-0 transition-all duration-300",
        "lg:ml-16",
        !sidebarCollapsed && "lg:ml-60"
      )}>
        <div className="w-full h-full min-h-0">
          {/* 
            条件渲染：移动端和桌面端使用不同布局
            类比 C++: if (isMobile) { renderMobileLayout(); } else { renderDesktopLayout(); }
          */}
          {focusSubtitles ? (
            <div className="flex flex-col h-full min-h-0 lg:h-[calc(100vh-4rem)]">
              {/* 移动端：显示小视频窗口；桌面端：隐藏视频 (1px) */}
              <div className={cn(
                "overflow-hidden shrink-0 transition-all duration-300",
                "h-[1px]" // 强制设为 1px，移动端也不显示视频画面
              )}>
                <VideoPlayer
                  key={videoId}
                  ref={playerRef}
                  src={playUrl || video.videoUrl}
                  currentTime={currentTime}
                  onTimeUpdate={setCurrentTime}
                  onDurationChange={setDuration}
                  onAnalyzeSentence={handleAnalyzeSentence}
                  hideInternalControls={true} // 移动端显示内部控制，桌面端隐藏
                  playbackRate={playbackRate}
                  volume={volume}
                  onVolumeChange={setVolume}
                  suspendVideo={shouldSuspendVideo}
                  forceDetach={isSettingsOpen}
                  carryoverTimeOnSourceSwitch={!isLiveDemoMode}
                  onPlay={() => setIsPlayingUI(true)}
                  onPause={() => setIsPlayingUI(false)}
                  onEnded={handleAutoPlayNext}
                />
              </div>

              <div className="flex-1 overflow-hidden">
                <ErrorBoundary>
                  <SubtitlePanel
                      subtitles={subtitles}
                      subtitlesLoading={subtitlesLoading}
                      currentTime={currentTime}
                      onSeek={handleSeek}
                    showFurigana={showFurigana}
                    showRomaji={showRomaji}
                    showChinese={showChinese}
                    videoId={videoId}
                    hideUnderline={hideUnderline}
                    hideGrammarBackground={hideGrammarBackground}
                    hideShadowingSpotlight={hideShadowingSpotlight}
                    shadowingMode={shadowingMode}
                    showOnlyChinese={showOnlyChinese}
                    highlightNewWords={highlightNewWords}
                    vocabulary={vocabulary}
                    tokenizer={tokenizer}
                    tokenizerError={tokenizerError}
                    setShowFurigana={setShowFurigana}
                    setShowRomaji={setShowRomaji}
                    setShowChinese={setShowChinese}
                    setHideUnderline={setHideUnderline}
                    setHideGrammarBackground={setHideGrammarBackground}
                    setHideShadowingSpotlight={setHideShadowingSpotlight}
                    setShadowingMode={setShadowingMode}
                    setShowOnlyChinese={setShowOnlyChinese}
                    setHighlightNewWords={setHighlightNewWords}
                    theme={theme}
                    setTheme={setTheme}
                    onOpenSettings={() => setIsSettingsOpen(true)}
                    onGoHome={() => navigate(isLiveDemoMode ? "/?live=1" : "/")}
                    isLiked={isLiked}
                    onToggleLike={toggleLike}
                    onAnalyzeSentence={handleAnalyzeSentence}
                    fontSize={fontSize}
                    blurStrength={blurStrength}
                    setFontSize={setFontSize}
                    setBlurStrength={setBlurStrength}
                  />
                </ErrorBoundary>
              </div>

              <div className="shrink-0 p-3 pb-4 bg-background/95 backdrop-blur border-t border-border">
                <div className="px-1 pt-1">
                  <Slider
                    value={[currentTime]}
                    max={duration || 100}
                    step={0.1}
                    onValueChange={(v) => handleSeek(v[0])}
                    className="cursor-pointer"
                  />
                </div>
                <div className="pt-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={togglePlayUI}
                      className="h-10 w-10 rounded-full bg-white border border-border shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90"
                    >
                      {isPlayingUI ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={rewind5UI}
                      className="h-10 w-10 rounded-full bg-white border border-border shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <span className="text-muted-foreground text-sm ml-2">
                      {Math.floor(currentTime / 60)}:{Math.floor(currentTime % 60).toString().padStart(2, "0")} / {Math.floor(duration / 60)}:{Math.floor(duration % 60).toString().padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
                        const next = rates[(rates.indexOf(playbackRate) + 1) % rates.length];
                        setPlaybackRate(next);
                      }}
                      className="h-10 px-2 min-w-[3rem] font-medium rounded-full bg-white border border-border shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90"
                      title="播放速度"
                    >
                      {playbackRate}x
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleSentenceLoop}
                      className={`h-10 w-10 rounded-full bg-white border border-border shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90 ${sentenceLoop ? "bg-muted" : ""}`}
                      title="单句循环"
                    >
                      <Repeat1 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setFullLoop((v) => !v)}
                      className={`h-10 w-10 rounded-full bg-white border border-border shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90 ${fullLoop ? "bg-muted" : ""}`}
                      title="全文循环"
                    >
                      <Repeat className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : isMobile ? (
            // ===== 移动端布局：垂直堆叠 =====
            <div className="flex flex-col h-full min-h-0">
              {/* 视频播放器 */}
              <div className="w-full shrink-0 h-[clamp(220px,42vh,360px)] max-[390px]:h-[clamp(190px,38vh,290px)]">
                <VideoPlayer
                  key={videoId}
                  ref={playerRef}  // 传递引用，让父组件能控制子组件
                  src={playUrl || video.videoUrl}
                  currentTime={currentTime}
                  onTimeUpdate={setCurrentTime}  // 子组件通过此回调更新父组件状态
                  onDurationChange={setDuration}
                  playbackRate={playbackRate}
                  volume={volume}
                  onVolumeChange={setVolume}
                  suspendVideo={shouldSuspendVideo}
                  forceDetach={isSettingsOpen}
                  carryoverTimeOnSourceSwitch={!isLiveDemoMode}
                  onPlay={() => setIsPlayingUI(true)}
                  onPause={() => setIsPlayingUI(false)}
                  onEnded={handleAutoPlayNext}
                  onAnalyzeSentence={handleAnalyzeSentence} // Add onAnalyzeSentence prop
                />
              </div>

              {/* 字幕面板 - 占据剩余高度 */}
                <div className="flex-1 overflow-hidden">
                  <ErrorBoundary>
                    <SubtitlePanel
                      subtitles={subtitles}
                      subtitlesLoading={subtitlesLoading}
                      currentTime={currentTime}
                      onSeek={handleSeek}
                      showFurigana={showFurigana}
                      showRomaji={showRomaji}
                      showChinese={showChinese}
                      videoId={videoId}
                      hideUnderline={hideUnderline}
                      hideGrammarBackground={hideGrammarBackground}
                      hideShadowingSpotlight={hideShadowingSpotlight}
                      shadowingMode={shadowingMode}
                      showOnlyChinese={showOnlyChinese}
                      highlightNewWords={highlightNewWords}
                      tokenizer={tokenizer}
                      setShowFurigana={setShowFurigana}
                      setShowRomaji={setShowRomaji}
                      setShowChinese={setShowChinese}
                      setHideUnderline={setHideUnderline}
                      setHideGrammarBackground={setHideGrammarBackground}
                      setHideShadowingSpotlight={setHideShadowingSpotlight}
                      setShadowingMode={setShadowingMode}
                      setShowOnlyChinese={setShowOnlyChinese}
                      setHighlightNewWords={setHighlightNewWords}
                      theme={theme}
                      setTheme={setTheme}
                      onOpenSettings={() => setIsSettingsOpen(true)}
                      onGoHome={() => navigate(isLiveDemoMode ? "/?live=1" : "/")}
                      isLiked={isLiked}
                      onToggleLike={toggleLike}
                      onAnalyzeSentence={handleAnalyzeSentence}
                      fontSize={fontSize}
                      blurStrength={blurStrength}
                      setFontSize={setFontSize}
                      setBlurStrength={setBlurStrength}
                    />
                  </ErrorBoundary>
                </div>
            </div>
          ) : (
            // ===== 桌面端布局：左右分栏 =====
            <div className="px-4 py-4 h-[calc(100vh-4rem)]">
              <div className="flex gap-4 h-full">
                {/* 左侧：视频 + 控制器 (约 60% 宽度) */}
                <div className="flex-[1.5] flex flex-col min-w-0">
                  {/* 视频容器：占据剩余高度，黑色背景，居中显示视频 */}
                  <div className="flex-1 bg-black rounded-xl overflow-hidden relative">
                    <VideoPlayer
                    key={videoId}
                    ref={playerRef}
                    src={playUrl || video.videoUrl}
                    currentTime={currentTime}
                    onTimeUpdate={setCurrentTime}
                    onDurationChange={setDuration}
                    onAnalyzeSentence={handleAnalyzeSentence}
                    hideInternalControls
                    playbackRate={playbackRate}
                    volume={volume}
                    onVolumeChange={setVolume}
                    suspendVideo={shouldSuspendVideo}
                    forceDetach={isSettingsOpen}
                    carryoverTimeOnSourceSwitch={!isLiveDemoMode}
                    onPlay={() => setIsPlayingUI(true)}
                    onPause={() => setIsPlayingUI(false)}
                    onEnded={handleAutoPlayNext}
                  />
                  </div>
                  
                  {/* 控制器：固定高度，位于视频下方 */}
                  <div className="h-auto shrink-0 mt-3 rounded-2xl bg-white/90 border border-border shadow-lg backdrop-blur-sm dark:bg-neutral-800/90 flex flex-col">
                    <div className="px-3 pt-2">
                      <Slider
                        value={[currentTime]}
                        max={duration || 100}
                        step={0.1}
                        onValueChange={(v) => handleSeek(v[0])}
                        className="cursor-pointer"
                      />
                    </div>
                    
                    {/* 播放控制栏：播放暂停居中，两侧分布 */}
                    <div className="px-4 pb-3 pt-1 flex items-center justify-between relative">
                      {/* 左侧：后退/前进 */}
                      <div className="flex items-center gap-2 flex-1 justify-start">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={rewind5UI}
                          className="h-9 w-9 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
                          title="后退5秒"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                        <span className="text-muted-foreground text-xs font-mono ml-1 hidden lg:inline-block">
                          {Math.floor(currentTime / 60)}:{Math.floor(currentTime % 60).toString().padStart(2, "0")} / {Math.floor(duration / 60)}:{Math.floor(duration % 60).toString().padStart(2, "0")}
                        </span>
                      </div>

                      {/* 中间：播放/暂停 (绝对居中) */}
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={togglePlayUI}
                          className="h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-md hover:bg-primary/90 hover:scale-105 transition-all"
                        >
                          {isPlayingUI ? <Pause className="h-6 w-6 fill-current" /> : <Play className="h-6 w-6 fill-current ml-0.5" />}
                        </Button>
                      </div>

                      {/* 右侧：功能按钮 */}
                      <div className="flex items-center gap-1.5 flex-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
                            const next = rates[(rates.indexOf(playbackRate) + 1) % rates.length];
                            setPlaybackRate(next);
                          }}
                          className="h-8 px-2 min-w-[2.5rem] text-xs font-medium rounded-full hover:bg-black/5 dark:hover:bg-white/10"
                          title="播放速度"
                        >
                          {playbackRate}x
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={toggleSentenceLoop}
                          className={cn("h-8 w-8 rounded-full hover:bg-black/5 dark:hover:bg-white/10", sentenceLoop && "text-primary bg-primary/10")}
                          title="单句循环"
                        >
                          <Repeat1 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setFullLoop((v) => !v)}
                          className={cn("h-8 w-8 rounded-full hover:bg-black/5 dark:hover:bg-white/10", fullLoop && "text-primary bg-primary/10")}
                          title="全文循环"
                        >
                          <Repeat className="h-4 w-4" />
                        </Button>
                        <div className="w-px h-4 bg-border mx-1" />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={handleAnalyzeSentence}
                          className="h-8 w-8 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
                          title="AI 整句解析"
                        >
                          <Bot className="h-4 w-4 text-purple-500" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setShadowingMode(!shadowingMode)}
                          className={cn(
                            "h-8 w-8 rounded-full hover:bg-black/5 dark:hover:bg-white/10",
                            shadowingMode && "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                          )}
                          title="影子跟读模式"
                        >
                          <Mic className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* 底部：字体大小和模糊控制 (从 Settings 移出) */}
                    <div className="px-4 py-2 border-t border-border/50 bg-muted/20 flex items-center justify-between gap-6 text-xs">
                      {/* 字体大小 */}
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-muted-foreground whitespace-nowrap">字号</span>
                        <Slider
                          value={[fontSize]}
                          min={0.8}
                          max={2.0}
                          step={0.1}
                          onValueChange={(v) => setFontSize(v[0])}
                          className="w-full max-w-[120px]"
                        />
                        <span className="text-muted-foreground w-8 text-right">{fontSize.toFixed(1)}x</span>
                      </div>
                      
                      {/* 分隔线 */}
                      <div className="w-px h-4 bg-border" />

                      {/* 音量 */}
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-muted-foreground whitespace-nowrap">音量</span>
                        <Slider
                          value={[volume]}
                          min={0}
                          max={1}
                          step={0.05}
                          onValueChange={(v) => setVolume(v[0])}
                          className="w-full max-w-[120px]"
                        />
                        <span className="text-muted-foreground w-10 text-right">{Math.round(volume * 100)}%</span>
                      </div>

                      {/* 分隔线 */}
                      <div className="w-px h-4 bg-border" />

                      {/* 模糊遮罩 */}
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-muted-foreground whitespace-nowrap">模糊</span>
                        <Slider
                          value={[blurStrength]}
                          min={0}
                          max={10}
                          step={1}
                          onValueChange={(v) => setBlurStrength(v[0])}
                          className="w-full max-w-[120px]"
                        />
                        <span className="text-muted-foreground w-8 text-right">{blurStrength > 0 ? `${blurStrength}px` : "Off"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 右侧：字幕面板 (约 40% 宽度) */}
                <div className="flex-1 min-w-0 h-full overflow-hidden">
                  <ErrorBoundary>
                    <SubtitlePanel
                      subtitles={subtitles}
                      subtitlesLoading={subtitlesLoading}
                      currentTime={currentTime}
                      onSeek={handleSeek}
                      showFurigana={showFurigana}
                      showRomaji={showRomaji}
                      showChinese={showChinese}
                      videoId={videoId}
                      hideUnderline={hideUnderline}
                      hideGrammarBackground={hideGrammarBackground}
                      hideShadowingSpotlight={hideShadowingSpotlight}
                      shadowingMode={shadowingMode}
                      showOnlyChinese={showOnlyChinese}
                      highlightNewWords={highlightNewWords}
                      vocabulary={vocabulary}
                      tokenizer={tokenizer}
                      tokenizerError={tokenizerError}
                      setShowFurigana={setShowFurigana}
                      setShowRomaji={setShowRomaji}
                      setShowChinese={setShowChinese}
                      setHideUnderline={setHideUnderline}
                      setHideGrammarBackground={setHideGrammarBackground}
                      setHideShadowingSpotlight={setHideShadowingSpotlight}
                      setShadowingMode={setShadowingMode}
                      setShowOnlyChinese={setShowOnlyChinese}
                      setHighlightNewWords={setHighlightNewWords}
                      theme={theme}
                      setTheme={setTheme}
                      onOpenSettings={() => setIsSettingsOpen(true)}
                      onGoHome={() => navigate(isLiveDemoMode ? "/?live=1" : "/")}
                      isLiked={isLiked}
                      onToggleLike={toggleLike}
                      onAnalyzeSentence={handleAnalyzeSentence}
                      fontSize={fontSize}
                      blurStrength={blurStrength}
                      setFontSize={setFontSize}
                      setBlurStrength={setBlurStrength}
                    />
                  </ErrorBoundary>
                </div>
              </div>
            </div>
          )}
        </div>
        <ShadowingPanel
          isVisible={shadowingMode}
          onClose={() => setShadowingMode(false)}
          currentSubtitle={currentSubtitle || null}
          onPrev={handlePrevSentence}
          onNext={handleNextSentence}
          isPlaying={isPlayingUI}
          onTogglePlay={togglePlayUI}
          onSeek={handleSeek}
          onPause={() => {
            playerRef.current?.pause();
            setIsPlayingUI(false);
          }}
          // Pass settings for subtitle rendering
          showFurigana={showFurigana}
          showRomaji={showRomaji}
          showChinese={showChinese}
          hideUnderline={hideUnderline}
          hideGrammarBackground={hideGrammarBackground}
          hideShadowingSpotlight={hideShadowingSpotlight}
          showOnlyChinese={showOnlyChinese}
          highlightNewWords={highlightNewWords}
          vocabulary={vocabulary}
          currentTime={currentTime}
          isLooping={sentenceLoop}
          onToggleLoop={toggleSentenceLoop}
          tokenizer={tokenizer}
        />
      </main>
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent
          className={cn(
            "z-[300] p-0 bg-transparent border-0 shadow-none focus:outline-none [&>button]:hidden",
            "w-[min(96vw,420px)] left-1/2 bottom-2 top-auto -translate-x-1/2 translate-y-0 max-h-[88dvh] overflow-visible",
            "sm:w-auto sm:max-w-[360px] sm:max-h-none sm:overflow-visible sm:left-[50%] sm:top-[50%] sm:bottom-auto sm:translate-x-[-50%] sm:translate-y-[-50%]",
          )}
        >
          <ReadingSettings
          showFurigana={showFurigana}
          setShowFurigana={setShowFurigana}
          showRomaji={showRomaji}
          setShowRomaji={setShowRomaji}
          showChinese={showChinese}
          setShowChinese={setShowChinese}
          hideUnderline={hideUnderline}
          setHideUnderline={setHideUnderline}
          hideGrammarBackground={hideGrammarBackground}
          setHideGrammarBackground={setHideGrammarBackground}
          hideShadowingSpotlight={hideShadowingSpotlight}
          setHideShadowingSpotlight={setHideShadowingSpotlight}
          showOnlyChinese={showOnlyChinese}
          setShowOnlyChinese={setShowOnlyChinese}
          highlightNewWords={highlightNewWords}
          setHighlightNewWords={setHighlightNewWords}
          theme={theme}
          setTheme={setTheme}
          onClose={() => setIsSettingsOpen(false)}
          fontSize={fontSize}
          setFontSize={setFontSize}
          blurStrength={blurStrength}
          setBlurStrength={setBlurStrength}
          volume={volume}
          setVolume={setVolume}
          autoPlayNext={autoPlayNext}
          setAutoPlayNext={setAutoPlayNext}
          isMobile={isMobile}
        />
        </DialogContent>
      </Dialog>
      <Dialog open={analysisOpen} onOpenChange={handleAnalysisOpenChange}>
        <DialogContent className="z-[310] max-w-xl max-h-[85vh] overflow-y-auto scrollbar-hide">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Bot className="h-6 w-6 text-primary" />
              AI 智能整句解析
            </DialogTitle>
          </DialogHeader>
          
          {isAnalyzing ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <div className="text-center space-y-1">
                <p className="font-medium">正在分析句子结构...</p>
                <p className="text-xs text-muted-foreground">正在拆解语法、标注词性与翻译</p>
              </div>
            </div>
          ) : !analysisResult ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>暂无分析结果</p>
              <Button variant="link" onClick={handleAnalyzeSentence} className="mt-2">
                重新尝试
              </Button>
            </div>
          ) : (
            <div className="space-y-6 pt-2">
              {/* 原文卡片 */}
              <div className="p-4 bg-muted/40 rounded-xl border border-border/50">
                <div className="text-xs font-bold text-muted-foreground mb-1.5 uppercase tracking-wider">Original Text</div>
                <div className="text-lg font-medium text-foreground leading-relaxed">{analysisResult.japanese}</div>
              </div>

              {/* 翻译 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-4 w-1 bg-primary rounded-full shadow-[0_0_8px_rgba(var(--primary),0.6)]" />
                  <span className="font-bold text-sm">中文翻译</span>
                </div>
                <div className="pl-3 text-foreground/90 leading-relaxed">{analysisResult.chinese}</div>
              </div>

              {/* 分词解析 */}
              {Array.isArray(analysisResult.tokens) && analysisResult.tokens.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-4 w-1 bg-sky-500 rounded-full" />
                    <span className="font-bold text-sm">分词详解</span>
                    <HoverCard openDelay={120}>
                      <HoverCardTrigger asChild>
                        <span className="text-xs text-muted-foreground cursor-help">词性</span>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-[220px] p-3">
                        <div className="text-xs font-semibold text-muted-foreground mb-2">词性颜色图例</div>
                        <div className="grid grid-cols-2 gap-2">
                          {posLegend.map((item) => (
                            <span key={item.label} className={`px-2 py-0.5 rounded text-[11px] font-medium border ${item.className}`}>
                              {item.label}
                            </span>
                          ))}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-1">
                    {analysisResult.tokens.map((t: any, i: number) => (
                      <div key={i} className="flex flex-col items-center bg-card border border-border/60 rounded-lg px-2.5 py-1.5 shadow-sm min-w-[3.5rem] transition-colors hover:border-primary/30 hover:bg-primary/5">
                        <span className="text-[10px] text-muted-foreground font-mono">{t.romaji}</span>
                        <span className="font-bold text-sm my-0.5">{t.word}</span>
                        <HoverCard openDelay={120}>
                          <HoverCardTrigger asChild>
                            <span
                              className={`text-[10px] px-1.5 rounded-full mt-0.5 border ${
                                posColorMap[t.partOfSpeech] || "bg-muted text-muted-foreground border-border"
                              }`}
                            >
                              {t.partOfSpeech}
                            </span>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-[220px] p-3">
                            <div className="text-xs font-semibold text-muted-foreground mb-2">词性颜色图例</div>
                            <div className="grid grid-cols-2 gap-2">
                              {posLegend.map((item) => (
                                <span key={item.label} className={`px-2 py-0.5 rounded text-[11px] font-medium border ${item.className}`}>
                                  {item.label}
                                </span>
                              ))}
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 语法重点 */}
              {analysisResult.grammarNotes && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-4 w-1 bg-amber-500 rounded-full" />
                    <span className="font-bold text-sm">语法重点</span>
                  </div>
                  <div className="pl-3 text-sm leading-relaxed text-foreground/90 bg-amber-50 dark:bg-amber-900/10 p-3 rounded-lg border border-amber-100 dark:border-amber-900/20 space-y-2">
                    <div>{analysisResult.grammarNotes}</div>
                    {analysisResult.grammarNotesZh && (
                      <div className="text-muted-foreground/90">{analysisResult.grammarNotesZh}</div>
                    )}
                  </div>
                </div>
              )}

              {/* 例句 */}
              {analysisResult.example && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-4 w-1 bg-emerald-500 rounded-full" />
                    <span className="font-bold text-sm">场景例句</span>
                  </div>
                  <div className="ml-1 pl-3 text-sm italic border-l-2 border-emerald-500/30 py-1 text-muted-foreground space-y-2">
                    <div>{analysisResult.example}</div>
                    {analysisResult.exampleZh && (
                      <div className="not-italic text-foreground/80">{analysisResult.exampleZh}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
